import wretch from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import type {
  PracticeResponse,
  PracticeStatusData,
  PracticeLeaderboardData,
  PracticeSessionData,
  PracticeAttemptRequest,
  PracticeAttemptResultData,
  PracticePresignedUrlData,
  PracticePresignedUrlParams,
  PracticeImagePayload,
  PracticePointsData,
  PracticeHistoryData,
  PracticeHistoryDetailData,
  PracticeQuestion,
  PracticeLeaderboardType
} from "./practiceService.types";

const BASE_URL = process.env.NEXT_PUBLIC_EXTERNAL_BFF_URL;

// Errors are reported by the caller, alongside the screen and operation.
const request = (accessToken: string, path: string) =>
  wretch(`${BASE_URL}/practices/v2/${path}`)
    .addon(QueryStringAddon)
    .headers({ Authorization: `Bearer ${accessToken}` })
    .options({ cache: "no-store" });

const unwrap = async <T>(
  response: Promise<PracticeResponse<T>>
): Promise<PracticeResponse<T>> => {
  const result = await response;
  if (!result.success)
    throw new Error(result.message || "Practice request failed");
  return result;
};

export const getPracticeStatus = (accessToken: string) =>
  unwrap(
    request(accessToken, "status")
      .get()
      .json<PracticeResponse<PracticeStatusData>>()
  );

export const getLeaderboardData = (
  accessToken: string,
  type: PracticeLeaderboardType
) =>
  unwrap(
    request(accessToken, "leaderboard")
      .query({ type })
      .get()
      .json<PracticeResponse<PracticeLeaderboardData>>()
  );

export const getPracticeSession = async (
  accessToken: string
): Promise<PracticeResponse<PracticeSessionData>> => {
  const result = await unwrap(
    request(accessToken, "sessions")
      .get()
      .json<PracticeResponse<PracticeSessionData>>()
  );
  const question = result.data.current_question;
  if (!question) throw new Error("Practice session has no current question");

  return {
    ...result,
    data: {
      ...result.data,
      current_question: normalizePracticeQuestion(question)
    }
  };
};

export const startNextPracticeQuestion = (accessToken: string) =>
  unwrap(
    request(accessToken, "start-next-question")
      .post({})
      .json<PracticeResponse<unknown>>()
  );

export const getPracticePresignedUrl = (
  accessToken: string,
  params: PracticePresignedUrlParams
) =>
  unwrap(
    request(accessToken, "presigned-url")
      .query(params)
      .get()
      .json<PracticeResponse<PracticePresignedUrlData>>()
  );

const imageToBlob = (image: string, fallbackMimeType: string): Blob => {
  const [header, data] = image.includes(",")
    ? image.split(",", 2)
    : ["", image];
  const mimeType = header.match(/^data:(.*?);base64$/)?.[1] ?? fallbackMimeType;
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
};

export const uploadPracticeImage = async (
  presignedUrl: string,
  image: PracticeImagePayload,
  mimeType = "image/jpeg"
): Promise<void> => {
  const body = typeof image === "string" ? imageToBlob(image, mimeType) : image;
  const response = await fetch(presignedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": body instanceof Blob && body.type ? body.type : mimeType
    },
    body
  });
  if (!response.ok)
    throw new Error(
      `Practice image upload failed with status ${response.status}`
    );
};

export const submitPracticeAttempt = (
  accessToken: string,
  payload: PracticeAttemptRequest
) =>
  unwrap(
    request(accessToken, "attempt")
      .post(payload)
      .json<PracticeResponse<PracticeAttemptResultData>>()
  );

export const getPracticePoints = (
  accessToken: string,
  practiceSessionId: string
) =>
  unwrap(
    request(accessToken, "points")
      .query({ practice_session_id: practiceSessionId })
      .get()
      .json<PracticeResponse<PracticePointsData>>()
  );

export const getPracticeHistory = (
  accessToken: string,
  limit: number,
  offset: number,
  hasAttempts = true
) =>
  unwrap(
    request(accessToken, "histories")
      .query({
        limit: String(limit),
        offset: String(offset),
        has_attempts: String(hasAttempts)
      })
      .get()
      .json<PracticeResponse<PracticeHistoryData[]>>()
  );

export const getPracticeHistoryDetail = (
  accessToken: string,
  historyId: string
) =>
  unwrap(
    request(accessToken, `histories/${encodeURIComponent(historyId)}`)
      .get()
      .json<PracticeResponse<PracticeHistoryDetailData>>()
  ).then((result) => ({
    ...result,
    data: {
      ...result.data,
      questions: (result.data?.questions ?? []).map((question) =>
        normalizePracticeQuestion(question)
      )
    }
  }));

const normalizePracticeQuestion = (
  question: PracticeQuestion
): PracticeQuestion => ({
  ...question,
  title: question.title ?? "",
  topic: question.topic ?? "",
  hint: question.hint ?? "",
  assets: question.assets ?? [],
  answers: (question.answers ?? []).map((answer) => ({
    ...answer,
    assets: answer.assets ?? []
  })),
  attempts: question.attempts ?? [],
  max_points: question.max_points ?? 0
});
